/**
 * The masthead's five questions and the row beneath an open one
 * (navigation re-cut, option A; docs/the-story.md, section 3).
 *
 * The masthead names the question; the row beneath it names the pages
 * that answer it, drawn from whichever module holds them. Which page
 * belongs to which question is `questions.ts`'s answer and no one
 * else's.
 */
import type { ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useMe } from "shared/api/useMe";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { useLicenseStatus } from "shared/licensing/useLicense";
import { MyWorkLink } from "shared/work/MyWorkLink";
import { activeTabTo } from "shared/layout/ModuleTabs";
import { QUESTIONS, questionFor, visibleDestinations } from "shared/layout/questions";

/** The five question links, for the masthead. A question with nothing
 *  this seat may open is left out. */
export function QuestionLinks({ className }: { className: string }) {
  const { t } = useTranslation();
  const me = useMe();
  const lic = useLicenseStatus();
  const { pathname } = useLocation();
  const current = questionFor(pathname);
  return (
    <>
      {QUESTIONS.map((q) => {
        if (q.key === "do") {
          // My Work keeps its own link: it carries the count of what is
          // asked of you (stitching review, 2026-10-02).
          return <MyWorkLink key={q.key} className={className} />;
        }
        const dests = visibleDestinations(q, me.data, lic.data);
        const first = dests[0];
        if (!first) return null;
        const active = current === q.key;
        return (
          <Link
            key={q.key}
            to={first.to}
            data-testid={`nav-question-${q.key}`}
            aria-current={active ? "page" : undefined}
            title={t(q.questionKey)}
            className={className}
          >
            {t(q.navKey)}
          </Link>
        );
      })}
    </>
  );
}

/** The row beneath the masthead for a page a question claims; `fallback`
 *  (the module tabs) for a page none does, such as Admin. */
export function QuestionTabs({ fallback }: { fallback: ReactNode }) {
  const { t } = useTranslation();
  const me = useMe();
  const lic = useLicenseStatus();
  const { pathname } = useLocation();
  const key = questionFor(pathname);
  const q = QUESTIONS.find((x) => x.key === key);
  if (!q) return <>{fallback}</>;
  const dests = visibleDestinations(q, me.data, lic.data);
  const active = activeTabTo(dests, pathname);
  return (
    <nav
      data-testid="question-tabs"
      data-question={q.key}
      aria-label={t(q.questionKey)}
      className="mt-1 mb-5 flex flex-wrap items-center gap-x-1 gap-y-0 border-b border-paper-2"
    >
      <span
        data-testid="question-tabs-heading"
        className="me-2 py-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400"
      >
        {t(q.questionKey)}
      </span>
      {dests.map((d) => {
        const isActive = d.to === active;
        return (
          <NavLink
            key={d.to}
            to={d.to}
            end
            data-testid="question-tab"
            data-to={d.to}
            data-active={isActive ? "true" : "false"}
            aria-current={isActive ? "page" : undefined}
            className={[
              "-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition",
              isActive
                ? "border-brand-brass text-brand-brassDark"
                : "border-transparent text-slate-500 hover:text-brand-brassDark",
            ].join(" ")}
          >
            <MarsadIcon name={d.icon} size={15} />
            {t(d.labelKey)}
          </NavLink>
        );
      })}
    </nav>
  );
}
